// dashboard-agent-ia/src/pages/ocass/MerchantDashboard.js
// Dashboard commerçant Ocass — authentification propre (token séparé du v1)
import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import OcassAuth from './OcassAuth'
import { titleCaseLive } from './utils/sectors'
import { REGIONS, getDepartments } from './utils/senegalGeo'
import {
  MdDashboard, MdInventory, MdShoppingCart, MdPeople, MdSettings, MdChat,
  MdLogout, MdChevronLeft, MdChevronRight, MdLightMode, MdDarkMode,
  MdSearch, MdAdd, MdEdit, MdDelete, MdClose, MdStorefront, MdSend, MdTrendingUp, MdPayments, MdCheckCircle,
  MdDownload, MdUpload, MdSwapHoriz,
  MdGavel, MdCancel, MdNotificationsNone, MdWorkspacePremium,
} from 'react-icons/md'

const API_URL = process.env.REACT_APP_API_URL || 'https://agent-ia-senegal.onrender.com'
const OCASS = `${API_URL}/ocass`
const TOKEN_KEY = 'ocass_merchant_token'

// ─── THÈME ───
const getTheme = (isDark) => ({
  bg: isDark ? 'linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#0f172a 100%)' : '#f1f5f9',
  sidebar: isDark ? 'linear-gradient(180deg,#0f172a 0%,#1e1b4b 60%,#0f172a 100%)' : 'rgba(255,255,255,0.97)',
  sidebarBorder: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
  header: isDark ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.95)',
  card: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.95)',
  popover: isDark ? '#1e1b4b' : '#ffffff',
  cardBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  cardShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)',
  text: isDark ? '#f1f5f9' : '#1e293b',
  textSub: isDark ? '#94a3b8' : '#64748b',
  textMuted: isDark ? '#475569' : '#94a3b8',
  navActive: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
  navHover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.08)',
  inputBg: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)',
  inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
  userBox: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  // Fond opaque pour les <select> et leurs <option> (fix lisibilité dark mode)
  selectBg: isDark ? '#1e1b4b' : '#ffffff',
  optionBg: isDark ? '#1e1b4b' : '#ffffff',
  optionText: isDark ? '#f1f5f9' : '#1e293b',
})

const fmtFCFA = (n) => (n || 0).toLocaleString('fr-FR') + ' FCFA'
const STATUS_LABELS = {
  pending_payment: 'En attente paiement', paid_pending_delivery: 'Payé — à livrer',
  delivered: 'Livré', partial_delivery: 'Livraison partielle', returned: 'Retourné',
}
const PRODUCT_STATUS = {
  disponible: { label: 'Disponible', color: '#10b981' },
  reserve: { label: 'Réservé', color: '#f59e0b' },
  vendu: { label: 'Vendu', color: '#64748b' },
  consignation: { label: 'Consignation', color: '#8b5cf6' },
  transfert: { label: 'Transfert', color: '#3b82f6' },
}

const apiClient = (token) => axios.create({ baseURL: OCASS, headers: { Authorization: `Bearer ${token}` } })

// ═══════════════════ LOGIN ═══════════════════
// ═══════════════════ LOGIN + INSCRIPTION ═══════════════════
// ═══════════════════ VUE D'ENSEMBLE ═══════════════════
const Overview = ({ token, th }) => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    apiClient(token).get('/merchant/api/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [token])

  if (loading) return <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p>
  if (!stats) return <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Erreur de chargement</p>

  const cards = [
    { label: 'Produits disponibles', value: stats.products?.available ?? 0, sub: `${stats.products?.total ?? 0} au total`, color: '#10b981', icon: <MdInventory size={24} /> },
    { label: "Commandes aujourd'hui", value: stats.orders?.today ?? 0, sub: `${stats.orders?.month ?? 0} ce mois`, color: '#3b82f6', icon: <MdShoppingCart size={24} /> },
    { label: 'Clients', value: stats.customers ?? 0, sub: 'Total', color: '#8b5cf6', icon: <MdPeople size={24} /> },
    { label: 'À livrer', value: stats.pendingDeliveries ?? 0, sub: 'Payé en attente', color: '#f59e0b', icon: <MdTrendingUp size={24} /> },
  ]

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 20, marginBottom: 28 }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, boxShadow: th.cardShadow }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: c.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.color }}>{c.icon}</div>
            </div>
            <p style={{ fontSize: 32, fontWeight: 800, color: th.text, lineHeight: 1 }}>{c.value}</p>
            <p style={{ fontSize: 13, color: th.text, marginTop: 8, fontWeight: 600 }}>{c.label}</p>
            <p style={{ fontSize: 12, color: th.textMuted, marginTop: 2 }}>{c.sub}</p>
          </div>
        ))}
      </div>
      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, boxShadow: th.cardShadow }}>
        <p style={{ fontWeight: 700, fontSize: 15, color: th.text, marginBottom: 4 }}>Chiffre d'affaires du mois</p>
        <p style={{ fontSize: 40, fontWeight: 800, color: '#10b981' }}>{fmtFCFA(stats.revenueMonth)}</p>
      </div>
    </div>
  )
}

// ═══════════════════ PRODUITS ═══════════════════
const Products = ({ token, th, isDark, merchant }) => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [categories, setCategories] = useState([])
  const [form, setForm] = useState({ name: '', category: '', condition: '', price: '', cost: '', stock: '', description: '', floorPriceOverride: '' })

  // Charger les catégories du secteur du commerçant
  useEffect(() => {
    const sectorId = merchant?.sectorId
    if (!sectorId) return
    apiClient(token).get('/reference/categories', { params: { sector: sectorId } })
      .then(r => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {})
  }, [token, merchant])

  const load = useCallback(() => {
    setLoading(true)
    const params = {}
    if (filterStatus !== 'all') params.status = filterStatus
    if (search) params.search = search
    apiClient(token).get('/merchant/api/products', { params }).then(r => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [token, filterStatus, search])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', category: '', condition: '', price: '', cost: '', stock: '', description: '', floorPriceOverride: '' })
    setShowForm(true)
  }
  const openEdit = (p) => {
    setEditing(p)
    setForm({ name: p.name, category: p.category || '', condition: p.condition || '', price: p.price, cost: p.cost || '', stock: p.stock ?? '', description: p.description || '', floorPriceOverride: p.floorPriceOverride ?? '' })
    setShowForm(true)
  }
  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price), cost: Number(form.cost) || 0, stock: form.stock === '' ? 1 : Number(form.stock) }
      if (form.floorPriceOverride === '') payload.floorPriceOverride = null
      else payload.floorPriceOverride = Number(form.floorPriceOverride)
      if (editing) await apiClient(token).put(`/merchant/api/products/${editing._id}`, payload)
      else await apiClient(token).post('/merchant/api/products', payload)
      setShowForm(false); load()
    } catch (e) { alert(e.response?.data?.error || e.message) }
  }
  const remove = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return
    try { await apiClient(token).delete(`/merchant/api/products/${id}`); load() }
    catch (e) { alert(e.message) }
  }

  // Charger SheetJS (xlsx) à la demande depuis CDN
  const loadXLSX = () => new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX)
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'
    s.onload = () => resolve(window.XLSX)
    s.onerror = () => reject(new Error('Impossible de charger Excel'))
    document.head.appendChild(s)
  })

  // Export : télécharge le catalogue en .xlsx
  const exportExcel = async () => {
    try {
      const XLSX = await loadXLSX()
      const rows = products.map(p => ({
        Nom: p.name, Categorie: p.category || '', Etat: p.condition || '',
        'Prix de vente': p.price, "Prix d'achat": p.cost || 0, Stock: p.stock ?? 0,
        Description: p.description || '', Reference: p.ref,
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Produits')
      XLSX.writeFile(wb, `catalogue-ocass-${new Date().toISOString().slice(0, 10)}.xlsx`)
    } catch (e) { alert(e.message) }
  }

  // Télécharge un modèle Excel vide à remplir
  const downloadTemplate = async () => {
    try {
      const XLSX = await loadXLSX()
      const ws = XLSX.utils.json_to_sheet([
        { Nom: 'Exemple iPhone 13', Categorie: 'Téléphones', Etat: 'Très bon état', 'Prix de vente': 285000, "Prix d'achat": 240000, Stock: 1, Description: '128 Go, bleu' },
      ])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Produits')
      XLSX.writeFile(wb, 'modele-import-ocass.xlsx')
    } catch (e) { alert(e.message) }
  }

  // Import : lit le .xlsx et envoie les lignes au backend
  const importExcel = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const XLSX = await loadXLSX()
      const data = await file.arrayBuffer()
      const wb = XLSX.read(data)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws)
      if (!rows.length) { alert('Le fichier est vide'); return }
      // Mapper les colonnes (tolérant aux variantes de noms)
      const mapped = rows.map(r => {
        const get = (...keys) => { for (const k of keys) { const found = Object.keys(r).find(rk => rk.toLowerCase().trim() === k.toLowerCase()); if (found) return r[found] } return '' }
        return {
          name: get('Nom', 'name', 'produit'),
          category: get('Categorie', 'Catégorie', 'category'),
          condition: get('Etat', 'État', 'condition'),
          price: get('Prix de vente', 'Prix', 'price', 'prix vente'),
          cost: get("Prix d'achat", 'cost', 'achat'),
          stock: get('Stock', 'stock', 'quantite', 'quantité'),
          description: get('Description', 'description'),
        }
      })
      if (!window.confirm(`Importer ${mapped.length} produit(s) ? Les produits existants (même nom) seront mis à jour.`)) return
      const res = await apiClient(token).post('/merchant/api/products/bulk', { products: mapped })
      const r = res.data
      alert(`Import terminé ✅\n${r.created} créé(s), ${r.updated} mis à jour, ${r.skipped} ignoré(s).` + (r.errors?.length ? '\n\n' + r.errors.join('\n') : ''))
      load()
    } catch (err) { alert(err.response?.data?.error || err.message) }
    finally { e.target.value = '' }
  }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ width: 240, maxWidth: '100%', display: 'flex', alignItems: 'center', gap: 8, background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 10, padding: '0 12px' }}>
          <MdSearch size={18} style={{ color: th.textMuted }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: th.text, fontSize: 14, padding: '10px 0', minWidth: 0 }} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${th.cardBorder}`, background: th.selectBg, color: th.text, fontSize: 13, outline: 'none' }}>
          <option value="all">Tous statuts</option>
          {Object.entries(PRODUCT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={openCreate} style={{ padding: '10px 18px', borderRadius: 10, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MdAdd size={18} /> Ajouter
        </button>
        <button onClick={exportExcel} title="Exporter en Excel" style={{ padding: '10px 14px', borderRadius: 10, background: th.card, border: `1px solid ${th.cardBorder}`, color: th.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MdDownload size={18} /> Exporter
        </button>
        <label title="Importer depuis Excel" style={{ padding: '10px 14px', borderRadius: 10, background: th.card, border: `1px solid ${th.cardBorder}`, color: th.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MdUpload size={18} /> Importer
          <input type="file" accept=".xlsx,.xls,.csv" onChange={importExcel} style={{ display: 'none' }} />
        </label>
        <button onClick={downloadTemplate} title="Télécharger un modèle vide" style={{ padding: '10px 14px', borderRadius: 12, background: 'transparent', border: `1px dashed ${th.cardBorder}`, color: th.textMuted, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
          Modèle Excel
        </button>
      </div>

      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {products.map(p => {
            const st = PRODUCT_STATUS[p.status] || PRODUCT_STATUS.disponible
            return (
              <div key={p._id} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <p style={{ fontWeight: 700, fontSize: 15, color: th.text, margin: 0 }}>{p.name}</p>
                    <span style={{ fontSize: 11, color: th.textMuted, fontFamily: 'monospace' }}>{p.ref}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: st.color + '18', color: st.color }}>{st.label}</span>
                  </div>
                  <p style={{ fontSize: 13, color: th.textMuted, margin: '4px 0 0' }}>
                    {p.category || 'Sans catégorie'}
                    {p.cost > 0 && <> · Achat {fmtFCFA(p.cost)}</>}
                    <> · Reste : <span style={{ fontWeight: 700, color: (p.stock ?? 0) <= 0 ? '#ef4444' : (p.stock ?? 0) <= 3 ? '#f59e0b' : th.textSub }}>{p.stock ?? 0}</span></>
                    {(p.sold ?? 0) > 0 && <> · Vendu : <span style={{ fontWeight: 700, color: '#10b981' }}>{p.sold}</span></>}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontWeight: 800, fontSize: 16, color: '#10b981', margin: 0 }}>{fmtFCFA(p.price)}</p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(p)} style={{ padding: 8, borderRadius: 8, background: 'rgba(59,130,246,0.12)', color: '#3b82f6', border: 'none', cursor: 'pointer', display: 'flex' }}><MdEdit size={16} /></button>
                  <button onClick={() => remove(p._id)} style={{ padding: 8, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', cursor: 'pointer', display: 'flex' }}><MdDelete size={16} /></button>
                </div>
              </div>
            )
          })}
          {products.length === 0 && <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Aucun produit. Cliquez sur "Ajouter".</p>}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 }} onClick={() => setShowForm(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: isDark ? '#1e1b4b' : '#fff', borderRadius: 20, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: th.text, margin: 0 }}>{editing ? 'Modifier' : 'Nouveau produit'}</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: th.textMuted }}><MdClose size={22} /></button>
            </div>
            {/* Nom */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Nom du produit *</label>
              <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: titleCaseLive(e.target.value) }))}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            {/* Catégorie : menu déroulant des catégories du secteur */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Catégorie</label>
              {categories.length > 0 ? (
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                  <option value="">— Choisir une catégorie —</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__autre__">Autre...</option>
                </select>
              ) : (
                <input type="text" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              )}
              {form.category === '__autre__' && (
                <input type="text" placeholder="Nouvelle catégorie" autoFocus onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  style={{ width: '100%', marginTop: 8, padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              )}
            </div>
            {/* État du produit (avec casse) */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>État (ex: Très bon état)</label>
              <input type="text" value={form.condition} onChange={e => setForm(p => ({ ...p, condition: titleCaseLive(e.target.value) }))}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>

            {/* Bloc PRIX avec explications */}
            <div style={{ background: isDark ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.04)', border: `1px solid ${th.cardBorder}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: 0.5 }}>💰 Prix & rentabilité</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Prix d'achat (FCFA) *</label>
                  <input type="number" value={form.cost} onChange={e => setForm(p => ({ ...p, cost: e.target.value }))}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${!form.cost || Number(form.cost) <= 0 ? 'rgba(239,68,68,0.5)' : th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Prix affiché (FCFA) *</label>
                  <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <p style={{ fontSize: 11, color: th.textMuted, margin: '8px 0 0', lineHeight: 1.5 }}>
                🛡️ Le prix d'achat protège votre marge : l'agent ne vendra <b>jamais</b> en dessous. Indispensable.
              </p>
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Prix plancher (optionnel)</label>
                <input type="number" value={form.floorPriceOverride} onChange={e => setForm(p => ({ ...p, floorPriceOverride: e.target.value }))} placeholder="Laisser vide = automatique"
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                <p style={{ fontSize: 11, color: th.textMuted, margin: '6px 0 0', lineHeight: 1.5 }}>
                  Le prix le plus bas que l'agent peut accepter <b>seul</b>. En dessous, il vous demande votre accord. Si vide, calculé automatiquement à partir du prix d'achat.
                </p>
              </div>
            </div>

            {/* Stock */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Stock actuel (quantité)</label>
              <input type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Description</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
            <button onClick={save} disabled={!form.name || !form.price || !form.cost || Number(form.cost) <= 0}
              style={{ width: '100%', padding: 13, borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: (!form.name || !form.price || !form.cost || Number(form.cost) <= 0) ? 0.6 : 1 }}>
              {editing ? 'Enregistrer' : 'Ajouter le produit'}
            </button>
            {(!form.cost || Number(form.cost) <= 0) && (
              <p style={{ fontSize: 12, color: '#ef4444', textAlign: 'center', margin: '10px 0 0' }}>⚠️ Le prix d'achat est obligatoire pour protéger votre marge</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════ COMMANDES ═══════════════════
const Orders = ({ token, th }) => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  const load = useCallback(() => {
    setLoading(true)
    const params = filter !== 'all' ? { status: filter } : {}
    apiClient(token).get('/merchant/api/orders', { params }).then(r => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [token, filter])
  useEffect(() => { load() }, [load])

  const updateStatus = async (id, deliveryStatus) => {
    try { await apiClient(token).put(`/merchant/api/orders/${id}/status`, { deliveryStatus }); load() }
    catch (e) { alert(e.message) }
  }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', ...Object.keys(STATUS_LABELS)].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${filter === s ? '#3b82f6' : th.cardBorder}`, background: filter === s ? '#3b82f6' : th.card, color: filter === s ? '#fff' : th.textSub }}>
            {s === 'all' ? 'Toutes' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>
      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {orders.map(o => (
            <div key={o._id} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 14, padding: '14px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 14, color: th.text, margin: 0 }}>{o.orderNumber || o._id.slice(-6)} · {o.customerName || o.customerPhone}</p>
                  <p style={{ fontSize: 13, color: th.textMuted, margin: '4px 0 0' }}>{o.items?.length || 0} article(s) · {fmtFCFA(o.total)}</p>
                </div>
                <select value={o.deliveryStatus} onChange={e => updateStatus(o._id, e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 12, outline: 'none' }}>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
          ))}
          {orders.length === 0 && <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Aucune commande pour ce filtre.</p>}
        </div>
      )}
    </div>
  )
}

// ═══════════════════ EMPLOYÉS ═══════════════════
const Employees = ({ token, th, isDark }) => {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', role: 'vendeur', pin: '' })

  const load = useCallback(() => {
    setLoading(true)
    apiClient(token).get('/merchant/api/employees').then(r => setEmployees(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [token])
  useEffect(() => { load() }, [load])

  const save = async () => {
    try {
      await apiClient(token).post('/merchant/api/employees', form)
      setShowForm(false); setForm({ firstName: '', lastName: '', phone: '', role: 'vendeur', pin: '' }); load()
    } catch (e) { alert(e.response?.data?.error || e.message) }
  }
  const remove = async (id) => {
    if (!window.confirm('Supprimer cet employé ?')) return
    try { await apiClient(token).delete(`/merchant/api/employees/${id}`); load() }
    catch (e) { alert(e.message) }
  }

  const ROLE_LABELS = { vendeur: 'Vendeur', caissier: 'Caissier', livreur: 'Livreur', gerant: 'Gérant' }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button onClick={() => setShowForm(true)} style={{ padding: '10px 18px', borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <MdAdd size={18} /> Ajouter un employé
        </button>
      </div>
      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {employees.map(e => (
            <div key={e._id} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#3b82f622', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#3b82f6', fontSize: 16 }}>
                {e.firstName?.[0]?.toUpperCase() || 'E'}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: 15, color: th.text, margin: 0 }}>{e.firstName} {e.lastName}</p>
                <p style={{ fontSize: 13, color: th.textMuted, margin: '2px 0 0' }}>{e.phone} · {ROLE_LABELS[e.role] || e.role}</p>
              </div>
              <button onClick={() => remove(e._id)} style={{ padding: 8, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', cursor: 'pointer', display: 'flex' }}><MdDelete size={16} /></button>
            </div>
          ))}
          {employees.length === 0 && <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Aucun employé.</p>}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 }} onClick={() => setShowForm(false)}>
          <div onClick={ev => ev.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: isDark ? '#1e1b4b' : '#fff', borderRadius: 20, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: th.text, margin: 0 }}>Nouvel employé</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: th.textMuted }}><MdClose size={22} /></button>
            </div>
            {[
              { k: 'firstName', label: 'Prénom *' }, { k: 'lastName', label: 'Nom' },
              { k: 'phone', label: 'Téléphone *' }, { k: 'pin', label: 'PIN (code de connexion) *' },
            ].map(f => (
              <div key={f.k} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>{f.label}</label>
                <input value={form[f.k]} onChange={e => setForm(p => ({ ...p, [f.k]: e.target.value }))}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Rôle</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none' }}>
                {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <button onClick={save} disabled={!form.firstName || !form.phone || !form.pin}
              style={{ width: '100%', padding: 13, borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: !form.firstName || !form.phone || !form.pin ? 0.6 : 1 }}>
              Ajouter l'employé
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ═══════════════════ PARAMÈTRES ═══════════════════
// ═══════════════════ MAGASINS (multi-boutiques + transfert) ═══════════════════
const Shops = ({ token, th, isDark }) => {
  const [shops, setShops] = useState([])
  const [limit, setLimit] = useState(1)
  const [loading, setLoading] = useState(true)
  const [showShopForm, setShowShopForm] = useState(false)
  const [shopForm, setShopForm] = useState({ name: '', address: '', city: '', phone: '' })
  const [showTransfer, setShowTransfer] = useState(false)
  const [products, setProducts] = useState([])
  const [transfer, setTransfer] = useState({ fromShopId: '', toShopId: '', productId: '', quantity: '' })

  const load = useCallback(() => {
    setLoading(true)
    apiClient(token).get('/merchant/api/shops').then(r => { setShops(r.data.shops); setLimit(r.data.limit) }).catch(() => {}).finally(() => setLoading(false))
  }, [token])
  useEffect(() => { load() }, [load])

  const saveShop = async () => {
    if (!shopForm.name) return
    try {
      await apiClient(token).post('/merchant/api/shops', shopForm)
      setShowShopForm(false); setShopForm({ name: '', address: '', city: '', phone: '' }); load()
    } catch (e) { alert(e.response?.data?.error || e.message) }
  }
  const removeShop = async (s) => {
    if (!window.confirm(`Supprimer la boutique "${s.name}" ?`)) return
    try { await apiClient(token).delete(`/merchant/api/shops/${s._id}`); load() }
    catch (e) { alert(e.response?.data?.error || e.message) }
  }

  // Charger les produits de la boutique source quand elle change
  const loadProductsOfShop = (shopId) => {
    if (!shopId) { setProducts([]); return }
    apiClient(token).get('/merchant/api/products').then(r => {
      setProducts((r.data || []).filter(p => String(p.shopId) === String(shopId) && (p.stock || 0) > 0))
    }).catch(() => {})
  }

  const doTransfer = async () => {
    const { fromShopId, toShopId, productId, quantity } = transfer
    if (!fromShopId || !toShopId || !productId || !quantity) { alert('Remplissez tous les champs'); return }
    try {
      const res = await apiClient(token).post('/merchant/api/shops/transfer', { ...transfer, quantity: Number(quantity) })
      alert(res.data.message + ' ✅')
      setShowTransfer(false); setTransfer({ fromShopId: '', toShopId: '', productId: '', quantity: '' }); setProducts([]); load()
    } catch (e) { alert(e.response?.data?.error || e.message) }
  }

  const canAdd = limit === -1 || shops.length < limit

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: 0 }}>Mes magasins</h2>
          <p style={{ color: th.textMuted, fontSize: 13, margin: '4px 0 0' }}>
            {shops.length} boutique{shops.length > 1 ? 's' : ''}{limit !== -1 ? ` / ${limit} (selon votre plan)` : ' (illimité)'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {shops.length >= 2 && (
            <button onClick={() => { setShowTransfer(true); setProducts([]); setTransfer({ fromShopId: '', toShopId: '', productId: '', quantity: '' }) }}
              style={{ padding: '10px 16px', borderRadius: 12, background: th.card, border: `1px solid ${th.cardBorder}`, color: th.textSub, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <MdSwapHoriz size={18} /> Transférer du stock
            </button>
          )}
          <button onClick={() => setShowShopForm(true)} disabled={!canAdd} title={!canAdd ? 'Limite de votre plan atteinte' : ''}
            style={{ padding: '10px 18px', borderRadius: 12, background: canAdd ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : th.card, color: canAdd ? 'white' : th.textMuted, border: canAdd ? 'none' : `1px solid ${th.cardBorder}`, fontWeight: 700, fontSize: 13, cursor: canAdd ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 6 }}>
            <MdAdd size={18} /> Nouvelle boutique
          </button>
        </div>
      </div>

      {!canAdd && <div style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', padding: '10px 14px', borderRadius: 12, fontSize: 13, margin: '12px 0 0' }}>
        Vous avez atteint la limite de votre plan. Passez à un plan supérieur pour ajouter d'autres boutiques.
      </div>}

      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 20 }}>
          {shops.map(s => (
            <div key={s._id} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}><MdStorefront size={24} /></div>
                {s.isMain
                  ? <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>Principale</span>
                  : <button onClick={() => removeShop(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><MdDelete size={18} /></button>}
              </div>
              <p style={{ fontWeight: 700, fontSize: 16, color: th.text, margin: 0 }}>{s.name}</p>
              {s.address && <p style={{ fontSize: 13, color: th.textMuted, margin: '4px 0 0' }}>{s.address}</p>}
              <p style={{ fontSize: 13, color: th.textMuted, margin: '2px 0 0' }}>{s.city}</p>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${th.cardBorder}` }}>
                <span style={{ fontSize: 13, color: th.textSub }}><b style={{ color: th.text }}>{s.productCount}</b> produit{s.productCount > 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal nouvelle boutique */}
      {showShopForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 }} onClick={() => setShowShopForm(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 440, background: isDark ? '#1e1b4b' : '#fff', borderRadius: 20, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: th.text, margin: 0 }}>Nouvelle boutique</h2>
              <button onClick={() => setShowShopForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: th.textMuted }}><MdClose size={22} /></button>
            </div>
            {[{ k: 'name', label: 'Nom de la boutique *' }, { k: 'address', label: 'Adresse' }, { k: 'city', label: 'Ville' }, { k: 'phone', label: 'Téléphone' }].map(f => (
              <div key={f.k} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>{f.label}</label>
                <input value={shopForm[f.k]} onChange={e => setShopForm(s => ({ ...s, [f.k]: e.target.value }))}
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            ))}
            <button onClick={saveShop} disabled={!shopForm.name} style={{ width: '100%', padding: 13, borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: !shopForm.name ? 0.6 : 1 }}>Créer la boutique</button>
          </div>
        </div>
      )}

      {/* Modal transfert */}
      {showTransfer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50 }} onClick={() => setShowTransfer(false)}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, background: isDark ? '#1e1b4b' : '#fff', borderRadius: 20, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 800, color: th.text, margin: 0 }}>Transférer du stock</h2>
              <button onClick={() => setShowTransfer(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: th.textMuted }}><MdClose size={22} /></button>
            </div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Depuis la boutique</label>
            <select value={transfer.fromShopId} onChange={e => { setTransfer(t => ({ ...t, fromShopId: e.target.value, productId: '' })); loadProductsOfShop(e.target.value) }}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}>
              <option value="">— Choisir —</option>
              {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Produit à transférer</label>
            <select value={transfer.productId} onChange={e => setTransfer(t => ({ ...t, productId: e.target.value }))} disabled={!transfer.fromShopId}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 14, opacity: transfer.fromShopId ? 1 : 0.5 }}>
              <option value="">— Choisir —</option>
              {products.map(p => <option key={p._id} value={p._id}>{p.name} (stock : {p.stock})</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Vers la boutique</label>
            <select value={transfer.toShopId} onChange={e => setTransfer(t => ({ ...t, toShopId: e.target.value }))}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}>
              <option value="">— Choisir —</option>
              {shops.filter(s => String(s._id) !== String(transfer.fromShopId)).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Quantité</label>
            <input type="number" min="1" value={transfer.quantity} onChange={e => setTransfer(t => ({ ...t, quantity: e.target.value }))}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 20 }} />
            <button onClick={doTransfer} style={{ width: '100%', padding: 13, borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Transférer</button>
          </div>
        </div>
      )}
    </div>
  )
}

const SettingsTab = ({ token, th }) => {
  const [settings, setSettings] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    apiClient(token).get('/merchant/api/settings').then(r => setSettings(r.data)).catch(() => {})
  }, [token])

  const save = async () => {
    setSaving(true)
    try {
      await apiClient(token).put('/merchant/api/settings', settings)
      setSaved(true); setTimeout(() => setSaved(false), 2500)
    } catch (e) { alert(e.message) } finally { setSaving(false) }
  }

  if (!settings) return <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p>

  const upd = (k, v) => setSettings(s => ({ ...s, [k]: v }))
  const updConfig = (k, v) => setSettings(s => ({ ...s, config: { ...s.config, [k]: v } }))
  const updPayment = (k, v) => setSettings(s => ({ ...s, payment: { ...s.payment, [k]: v } }))

  // Upload + compression du logo en base64 (max ~200px, léger pour la base)
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { alert('Choisissez une image'); return }
    const reader = new FileReader()
    reader.onload = (ev) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const max = 200
        let { width, height } = img
        if (width > height) { if (width > max) { height = height * max / width; width = max } }
        else { if (height > max) { width = width * max / height; height = max } }
        canvas.width = width; canvas.height = height
        canvas.getContext('2d').drawImage(img, 0, 0, width, height)
        upd('logo', canvas.toDataURL('image/jpeg', 0.8))
      }
      img.src = ev.target.result
    }
    reader.readAsDataURL(file)
  }

  const field = (label, value, onChange, type = 'text') => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>{label}</label>
      <input type={type} value={value ?? ''} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
        style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
    </div>
  )

  return (
    <div style={{ padding: 28, maxWidth: 700 }}>
      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: th.text, marginTop: 0, marginBottom: 16 }}>Boutique</h3>
        {/* Logo */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 8 }}>Logo de la boutique</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 72, height: 72, borderRadius: 16, border: `1px solid ${th.cardBorder}`, background: th.inputBg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
              {settings.logo
                ? <img src={settings.logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <MdStorefront size={32} style={{ color: th.textMuted }} />}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ padding: '8px 16px', borderRadius: 10, background: 'rgba(59,130,246,0.12)', color: '#3b82f6', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-block' }}>
                Choisir une image
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
              </label>
              {settings.logo && <button onClick={() => upd('logo', '')} style={{ padding: '6px 12px', borderRadius: 8, background: 'transparent', color: '#ef4444', border: 'none', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}>Retirer le logo</button>}
            </div>
          </div>
        </div>
        {field('Nom de la boutique', settings.businessName, v => upd('businessName', titleCaseLive(v)))}
        {field('Nom du propriétaire', settings.ownerName, v => upd('ownerName', titleCaseLive(v)))}
        {field('📱 Numéro perso pour les alertes (≠ numéro du bot)', settings.notificationPhone, v => upd('notificationPhone', v))}
        {/* Région + Département (menus déroulants) */}
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Région</label>
            <select value={settings.region || ''} onChange={e => { upd('region', e.target.value); upd('department', '') }}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.selectBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
              <option value="">— Choisir —</option>
              {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ flex: 1, marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: th.textSub, marginBottom: 6 }}>Département</label>
            <select value={settings.department || ''} onChange={e => upd('department', e.target.value)} disabled={!settings.region}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.selectBg, color: th.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', opacity: settings.region ? 1 : 0.5 }}>
              <option value="">— Choisir —</option>
              {getDepartments(settings.region || '').map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        {field('Ville / Quartier', settings.city, v => upd('city', titleCaseLive(v)))}
        {field('Adresse précise', settings.address, v => upd('address', v))}
      </div>

      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: th.text, marginTop: 0, marginBottom: 16 }}>Négociation</h3>
        {field('Marge minimum (%)', settings.config?.minMargin, v => updConfig('minMargin', v), 'number')}
        {field('Commission consignation (%)', settings.config?.consignmentCommission, v => updConfig('consignmentCommission', v), 'number')}
        <p style={{ fontSize: 12, color: th.textMuted, marginTop: 8 }}>Le prix plancher par produit se définit dans la fiche produit. La marge minimum s'applique par défaut.</p>
      </div>

      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 18, padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: th.text, marginTop: 0, marginBottom: 16 }}>Paiements</h3>
        {field('Code marchand Wave', settings.payment?.waveMerchantCode, v => updPayment('waveMerchantCode', v))}
        {field('Code marchand Orange Money', settings.payment?.orangeMoneyMerchantCode, v => updPayment('orangeMoneyMerchantCode', v))}
      </div>

      <button onClick={save} disabled={saving}
        style={{ padding: '13px 28px', borderRadius: 12, background: saved ? '#10b981' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
        {saved ? '✓ Enregistré' : saving ? 'Enregistrement...' : 'Enregistrer'}
      </button>
    </div>
  )
}

// ═══════════════════ PLAYGROUND ═══════════════════
const Playground = ({ token, th, isDark, embedded }) => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)

  const send = async () => {
    if (!input.trim()) return
    const userMsg = { role: 'user', content: input }
    const newMessages = [...messages, userMsg]
    setMessages(newMessages); setInput(''); setLoading(true)
    try {
      const res = await apiClient(token).post('/merchant/playground', { message: input, history: messages })
      setMessages([...newMessages, { role: 'assistant', content: res.data.reply, agent: res.data.agent, intent: res.data.intent, state: res.data.state, data: res.data.data }])
    } catch (e) {
      setMessages([...newMessages, { role: 'assistant', content: 'Erreur: ' + (e.response?.data?.error || e.message), agent: 'error' }])
    } finally { setLoading(false) }
  }

  return (
    <div style={{ padding: embedded ? 16 : 28, maxWidth: embedded ? '100%' : 700, margin: '0 auto', display: 'flex', flexDirection: 'column', height: embedded ? '100%' : 'calc(100vh - 64px)', boxSizing: 'border-box' }}>
      {!embedded && (
        <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, padding: 16, marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: th.textSub, margin: 0 }}>💬 Testez votre assistant IA comme si vous étiez un client. Les messages ne sont pas enregistrés.</p>
        </div>
      )}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, padding: 4 }}>
        {messages.length === 0 && <p style={{ color: th.textMuted, textAlign: 'center', marginTop: 40 }}>Envoyez un message pour tester (ex: "bonjour, je cherche un iphone")</p>}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
            <div style={{ padding: '10px 14px', borderRadius: 14, background: m.role === 'user' ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : th.card, color: m.role === 'user' ? '#fff' : th.text, border: m.role === 'user' ? 'none' : `1px solid ${th.cardBorder}`, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {m.content}
            </div>
            {m.role === 'assistant' && m.agent !== 'error' && (() => {
              const AGENT_BADGE = {
                search:          { label: '🔍 Agent Recherche', color: '#3b82f6' },
                greeting:        { label: '🔍 Agent Recherche', color: '#3b82f6' },
                ask_price:       { label: '💰 Agent Négociateur', color: '#f59e0b' },
                negotiate:       { label: '💰 Agent Négociateur', color: '#f59e0b' },
                agree:           { label: '💰 Agent Négociateur', color: '#f59e0b' },
                ask_photo:       { label: '📷 Agent Catalogue', color: '#8b5cf6' },
                decline:         { label: '🤝 Agent Relation client', color: '#ec4899' },
                checkout:        { label: '🛒 Agent Commande', color: '#10b981' },
                payment_confirm: { label: '✅ Agent Paiement', color: '#10b981' },
                general:         { label: '🤖 Assistant', color: '#64748b' },
              }
              const b = AGENT_BADGE[m.intent] || { label: '🤖 Assistant', color: '#64748b' }
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '5px 0 0 8px' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: b.color }} />
                  <span style={{ fontSize: 11, color: b.color, fontWeight: 600 }}>{b.label}</span>
                  {m.intent && <span style={{ fontSize: 10, color: th.textMuted }}>· {m.intent}</span>}
                </div>
              )
            })()}
          </div>
        ))}
        {loading && <div style={{ alignSelf: 'flex-start', color: th.textMuted, fontSize: 13, padding: '8px 14px' }}>L'IA réfléchit...</div>}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} placeholder="Écrivez un message..."
          style={{ flex: 1, padding: '13px 16px', borderRadius: 12, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none' }} />
        <button onClick={send} disabled={loading || !input.trim()}
          style={{ padding: '0 20px', borderRadius: 12, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: loading || !input.trim() ? 0.6 : 1 }}>
          <MdSend size={20} />
        </button>
      </div>
    </div>
  )
}

// ═══════════════════ PAIEMENTS À CONFIRMER ═══════════════════
const Payments = ({ token, th }) => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [confirming, setConfirming] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    apiClient(token).get('/merchant/api/payments/pending').then(r => setPayments(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [token])
  useEffect(() => { load() }, [load])

  const confirm = async (id) => {
    if (!window.confirm('Confirmer la réception de ce paiement ? Le client recevra sa facture.')) return
    setConfirming(id)
    try {
      await apiClient(token).post(`/merchant/api/payments/${id}/confirm`)
      load()
    } catch (e) { alert(e.response?.data?.error || e.message) }
    finally { setConfirming(null) }
  }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, padding: 16, marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: th.textSub, margin: 0 }}>💰 Confirmez les paiements reçus. Le client recevra automatiquement sa facture PDF sur WhatsApp.</p>
      </div>
      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {payments.map(p => (
            <div key={p._id} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 14, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f59e0b22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                <MdPayments size={22} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, fontSize: 15, color: th.text, margin: 0 }}>{fmtFCFA(p.amount)}</p>
                <p style={{ fontSize: 13, color: th.textMuted, margin: '2px 0 0' }}>{p.customerPhone} · {p.provider} · {new Date(p.createdAt).toLocaleString('fr-FR')}</p>
              </div>
              <button onClick={() => confirm(p._id)} disabled={confirming === p._id}
                style={{ padding: '9px 16px', borderRadius: 10, background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: confirming === p._id ? 0.6 : 1 }}>
                <MdCheckCircle size={17} /> {confirming === p._id ? '...' : 'Confirmer'}
              </button>
            </div>
          ))}
          {payments.length === 0 && <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Aucun paiement en attente.</p>}
        </div>
      )}
    </div>
  )
}

// ═══════════════════ DEMANDES DE PRIX (zone grise) ═══════════════
const Approvals = ({ token, th }) => {
  const [approvals, setApprovals] = useState([])
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    apiClient(token).get('/merchant/api/approvals/pending').then(r => setApprovals(r.data)).catch(() => {}).finally(() => setLoading(false))
  }, [token])
  useEffect(() => { load() }, [load])

  const decide = async (id, decision) => {
    setActing(id)
    try {
      await apiClient(token).post(`/merchant/api/approvals/${id}/decide`, { decision })
      load()
    } catch (e) { alert(e.response?.data?.error || e.message) }
    finally { setActing(null) }
  }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, padding: 16, marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: th.textSub, margin: 0 }}>⚖️ Quand un client propose un prix bas, l'IA vous demande votre accord ici. Acceptez ou refusez — le client est notifié automatiquement sur WhatsApp.</p>
      </div>
      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {approvals.map(a => (
            <div key={a._id} style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 14, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <p style={{ fontWeight: 700, fontSize: 15, color: th.text, margin: 0 }}>{a.productName} <span style={{ fontSize: 11, color: th.textMuted, fontFamily: 'monospace' }}>{a.productRef}</span></p>
                  <p style={{ fontSize: 13, color: th.textMuted, margin: '4px 0 0' }}>Client: {a.customerPhone}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, padding: '12px 14px', background: th.inputBg, borderRadius: 10 }}>
                <div><p style={{ fontSize: 11, color: th.textMuted, margin: 0 }}>Prix affiché</p><p style={{ fontSize: 15, fontWeight: 700, color: th.text, margin: '2px 0 0' }}>{fmtFCFA(a.sellPrice)}</p></div>
                <div><p style={{ fontSize: 11, color: th.textMuted, margin: 0 }}>Client propose</p><p style={{ fontSize: 15, fontWeight: 800, color: '#f59e0b', margin: '2px 0 0' }}>{fmtFCFA(a.requestedPrice)}</p></div>
                <div><p style={{ fontSize: 11, color: th.textMuted, margin: 0 }}>Min. conseillé</p><p style={{ fontSize: 15, fontWeight: 700, color: th.text, margin: '2px 0 0' }}>{fmtFCFA(a.minPrice)}</p></div>
                {a.cost > 0 && <div><p style={{ fontSize: 11, color: th.textMuted, margin: 0 }}>Prix d'achat</p><p style={{ fontSize: 15, fontWeight: 700, color: th.textMuted, margin: '2px 0 0' }}>{fmtFCFA(a.cost)}</p></div>}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => decide(a._id, 'approved')} disabled={acting === a._id}
                  style={{ flex: 1, padding: '11px', borderRadius: 10, background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: acting === a._id ? 0.6 : 1 }}>
                  <MdCheckCircle size={18} /> Accepter {fmtFCFA(a.requestedPrice)}
                </button>
                <button onClick={() => decide(a._id, 'refused')} disabled={acting === a._id}
                  style={{ flex: 1, padding: '11px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: acting === a._id ? 0.6 : 1 }}>
                  <MdCancel size={18} /> Refuser
                </button>
              </div>
            </div>
          ))}
          {approvals.length === 0 && <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Aucune demande de prix en attente.</p>}
        </div>
      )}
    </div>
  )
}

// ═══════════════════ MON ABONNEMENT ═══════════════════
const SubscriptionTab = ({ token, th, subStatus }) => {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(null)
  const [error, setError] = useState('')
  useEffect(() => {
    apiClient(token).get('/subscription/me')
      .then(r => {
        const p = r.data.plans
        // plans peut être un tableau [{key,...}] ou un objet {starter:{...}}
        if (Array.isArray(p)) setPlans(p)
        else setPlans(Object.keys(p || {}).map(k => ({ key: k, ...p[k] })))
      })
      .catch(() => {}).finally(() => setLoading(false))
  }, [token])

  const handlePay = async (planKey) => {
    setPaying(planKey); setError('')
    try {
      const res = await apiClient(token).post('/payments/checkout', { plan: planKey })
      if (res.data.payment_url) {
        // Ouvrir PayTech dans un popup pour ne pas quitter la plateforme
        const popup = window.open(res.data.payment_url, 'PayTech Payment',
          'width=600,height=720,scrollbars=yes,resizable=yes,top=80,left=' + Math.max(0, (window.screen.width / 2 - 300)))
        if (!popup) {
          // Popup bloqué → fallback redirection plein écran
          window.location.href = res.data.payment_url
        } else {
          // Surveiller la fermeture du popup pour rafraîchir le statut
          const timer = setInterval(() => {
            if (popup.closed) { clearInterval(timer); window.location.reload() }
          }, 1000)
        }
      } else setError('Erreur lors de la création du paiement')
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de connexion au paiement')
    } finally { setPaying(null) }
  }

  const META = {
    starter: { color: '#3b82f6' },
    pro: { color: '#8b5cf6', popular: true },
    premium: { color: '#f59e0b' },
  }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <h2 style={{ color: th.text, fontSize: 24, fontWeight: 800, margin: 0 }}>Choisir un plan</h2>
        <p style={{ color: th.textSub, fontSize: 14, margin: '8px 0 0' }}>Choisissez le plan adapté à votre activité</p>
        <p style={{ color: th.textMuted, fontSize: 12, margin: '4px 0 0' }}>Paiement sécurisé — Wave · Orange Money · Carte bancaire</p>
      </div>

      {subStatus && (
        <div style={{ textAlign: 'center', margin: '16px 0 24px' }}>
          <span style={{ fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 20,
            background: subStatus.kind === 'trial' ? 'rgba(245,158,11,0.12)' : subStatus.kind === 'paid' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
            color: subStatus.kind === 'trial' ? '#f59e0b' : subStatus.kind === 'paid' ? '#10b981' : '#ef4444' }}>
            {subStatus.kind === 'trial' ? `⏰ Essai gratuit — ${subStatus.daysLeft}j restants` : subStatus.kind === 'paid' ? `✓ Plan ${subStatus.plan} actif — ${subStatus.daysLeft}j` : '⚠️ Abonnement expiré'}
          </span>
        </div>
      )}

      {error && <div style={{ maxWidth: 600, margin: '0 auto 20px', background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)', padding: '10px 14px', borderRadius: 12, fontSize: 13, textAlign: 'center' }}>{error}</div>}

      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, maxWidth: 980, margin: '0 auto' }}>
          {plans.sort((a, b) => (a.price || 0) - (b.price || 0)).map(p => {
            const meta = META[p.key] || { color: '#3b82f6' }
            const isCurrent = subStatus?.kind === 'paid' && subStatus.plan === p.key
            return (
              <div key={p.key} style={{ position: 'relative', background: th.card, border: `2px solid ${isCurrent ? '#10b981' : meta.popular ? meta.color + '66' : th.cardBorder}`, borderRadius: 18, padding: 24, boxShadow: meta.popular ? `0 8px 32px ${meta.color}22` : 'none' }}>
                {meta.popular && <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', fontSize: 12, fontWeight: 700, color: '#fff', background: meta.color, padding: '4px 14px', borderRadius: 20 }}>⭐ Plus populaire</div>}
                {isCurrent && <div style={{ position: 'absolute', top: -13, right: 16, fontSize: 12, fontWeight: 700, color: '#fff', background: '#10b981', padding: '4px 14px', borderRadius: 20 }}>✓ Plan actuel</div>}
                <p style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: meta.color, margin: 0 }}>{p.name}</p>
                <p style={{ margin: '8px 0 16px', color: th.text }}><span style={{ fontSize: 30, fontWeight: 800 }}>{(p.price || 0).toLocaleString('fr-FR')}</span> <span style={{ fontSize: 13, color: th.textMuted }}>FCFA/mois</span></p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  <Feature th={th} ok>{p.maxShops === -1 ? 'Boutiques illimitées' : `${p.maxShops} boutique${p.maxShops > 1 ? 's' : ''}`}</Feature>
                  <Feature th={th} ok>{p.maxEmployees === -1 ? 'Employés illimités' : `${p.maxEmployees} employé${p.maxEmployees > 1 ? 's' : ''}`}</Feature>
                  {(p.features || []).map((f, i) => <Feature key={i} th={th} ok>{f}</Feature>)}
                </div>
                <button onClick={() => handlePay(p.key)} disabled={!!paying || isCurrent}
                  style={{ width: '100%', padding: 13, borderRadius: 12, border: 'none', fontWeight: 700, fontSize: 14, cursor: isCurrent ? 'default' : 'pointer',
                    color: '#fff', background: isCurrent ? '#10b981' : `linear-gradient(135deg,${meta.color},${meta.color}bb)`, opacity: paying && paying !== p.key ? 0.5 : 1 }}>
                  {isCurrent ? '✓ Actif' : paying === p.key ? '...' : 'Choisir ce plan →'}
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ marginTop: 32, paddingTop: 20, borderTop: `1px solid ${th.cardBorder}` }}>
        <p style={{ fontSize: 12, color: th.textMuted, textAlign: 'center', margin: '0 0 14px' }}>Paiement sécurisé via</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          {[
            { name: 'Orange Money', src: 'https://paytech.sn/assets/images/ico-orange.png' },
            { name: 'Wave', src: 'https://paytech.sn/assets/images/wave-sn.png' },
            { name: 'Visa', src: 'https://www.weareplanet.com/sites/default/files/2025-02/visa.svg' },
            { name: 'Mastercard', src: 'https://www.weareplanet.com/sites/default/files/2025-02/mastercard.svg' },
          ].map((p, i) => (
            <div key={i} style={{ width: 60, height: 60, borderRadius: 12, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
              <img src={p.src} alt={p.name} title={p.name} style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
const Feature = ({ th, ok, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <MdCheckCircle size={16} color={ok ? '#10b981' : th.textMuted} />
    <span style={{ fontSize: 13, color: th.textSub }}>{children}</span>
  </div>
)

// ═══════════════════ NOTIFICATIONS ═══════════════════
const NOTIF_META = {
  new_order:             { icon: '🛒', title: 'Nouvelle commande' },
  new_message:           { icon: '💬', title: 'Nouveau message' },
  payment_pending:       { icon: '💳', title: 'Paiement à confirmer' },
  payment_confirmed:     { icon: '✅', title: 'Paiement reçu' },
  price_approval:        { icon: '🟡', title: 'Demande de prix' },
  stock_alert:           { icon: '📦', title: 'Stock bas' },
  daily_report:          { icon: '📊', title: 'Rapport du jour' },
  subscription_reminder: { icon: '⏰', title: 'Abonnement' },
  custom:                { icon: '🔔', title: 'Notification' },
}
const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000)
  if (s < 60) return "à l'instant"
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`
  return `il y a ${Math.floor(s / 86400)} j`
}

const NotificationsTab = ({ token, th, isDark }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const load = useCallback(() => {
    setLoading(true)
    apiClient(token).get('/merchant/notifications')
      .then(r => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false))
  }, [token])
  useEffect(() => { load() }, [load])

  return (
    <div style={{ padding: 28, maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Notifications</h2>
      <p style={{ color: th.textMuted, fontSize: 13, margin: '0 0 24px' }}>Tout ce qui se passe dans votre boutique, en temps réel.</p>
      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p>
        : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: th.textMuted }}>
            <MdNotificationsNone size={48} style={{ opacity: 0.3 }} />
            <p style={{ marginTop: 12 }}>Aucune notification pour le moment</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(n => {
              const meta = NOTIF_META[n.type] || NOTIF_META.custom
              return (
                <div key={n._id} style={{ display: 'flex', gap: 14, padding: 16, borderRadius: 14, background: th.card, border: `1px solid ${th.cardBorder}`, alignItems: 'flex-start' }}>
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{meta.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 14, fontWeight: 700, color: th.text, margin: 0 }}>{meta.title}</p>
                    <p style={{ fontSize: 13, color: th.textSub, margin: '3px 0 0', lineHeight: 1.5 }}>{n.message}</p>
                    <p style={{ fontSize: 12, color: th.textMuted, margin: '6px 0 0' }}>{timeAgo(n.createdAt)}</p>
                  </div>
                  {!n.readAt && <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#3b82f6', flexShrink: 0, marginTop: 6 }} />}
                </div>
              )
            })}
          </div>
        )}
    </div>
  )
}

// ═══════════════════ COMPTABILITÉ ═══════════════════
const AccountingTab = ({ token, th, isDark }) => {
  const [period, setPeriod] = useState('month')
  const [data, setData] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState('dashboard') // dashboard | invoices

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([
      apiClient(token).get('/merchant/accounting/overview', { params: { period } }),
      apiClient(token).get('/merchant/accounting/invoices'),
    ]).then(([o, inv]) => { setData(o.data); setInvoices(inv.data || []) })
      .catch(() => {}).finally(() => setLoading(false))
  }, [token, period])
  useEffect(() => { load() }, [load])

  const fmt = (n) => (n || 0).toLocaleString('fr-FR')
  const maxRev = data?.series?.length ? Math.max(...data.series.map(s => s.revenue), 1) : 1

  const downloadInvoicePdf = async (id, number) => {
    try {
      const res = await apiClient(token).get(`/merchant/accounting/invoices/${id}/pdf`, { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    } catch (e) { alert('Impossible de générer le PDF pour le moment.') }
  }

  const KPI = ({ label, value, suffix, color, sub }) => (
    <div style={{ flex: 1, minWidth: 160, background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, padding: 20 }}>
      <p style={{ fontSize: 13, color: th.textMuted, margin: 0 }}>{label}</p>
      <p style={{ fontSize: 26, fontWeight: 800, color: color || th.text, margin: '6px 0 0' }}>{value}<span style={{ fontSize: 13, fontWeight: 600, color: th.textMuted }}>{suffix}</span></p>
      {sub && <p style={{ fontSize: 12, color: th.textSub, margin: '4px 0 0' }}>{sub}</p>}
    </div>
  )

  const INV_TYPES = { facture: 'Facture', devis: 'Devis', bon_livraison: 'Bon de livraison', recu: 'Reçu', bon_commande: 'Bon de commande' }

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: 0 }}>Comptabilité</h2>
          <p style={{ color: th.textMuted, fontSize: 13, margin: '2px 0 0' }}>Vos finances et vos documents en un coup d'œil.</p>
        </div>
        {/* Bascule Dashboard / Factures */}
        <div style={{ display: 'flex', background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 10, padding: 3 }}>
          <button onClick={() => setView('dashboard')} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: view === 'dashboard' ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : 'transparent', color: view === 'dashboard' ? '#fff' : th.textSub }}>Tableau financier</button>
          <button onClick={() => setView('invoices')} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: view === 'invoices' ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : 'transparent', color: view === 'invoices' ? '#fff' : th.textSub }}>Factures</button>
        </div>
      </div>

      {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p> : view === 'dashboard' ? (
        <>
          {/* Sélecteur de période */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[['week', '7 jours'], ['month', '30 jours'], ['year', '12 mois']].map(([k, lbl]) => (
              <button key={k} onClick={() => setPeriod(k)} style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${period === k ? '#3b82f6' : th.cardBorder}`, background: period === k ? 'rgba(59,130,246,0.1)' : th.card, color: period === k ? '#3b82f6' : th.textSub, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{lbl}</button>
            ))}
          </div>

          {/* KPI */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
            <KPI label="Chiffre d'affaires" value={fmt(data?.revenue)} suffix=" F" color="#3b82f6" />
            <KPI label="Bénéfice estimé" value={fmt(data?.profit)} suffix=" F" color="#10b981" sub={`Marge ${data?.margin || 0}%`} />
            <KPI label="Ventes" value={fmt(data?.count)} sub={`Panier moyen ${fmt(data?.avgBasket)} F`} />
            <KPI label="En attente de paiement" value={fmt(data?.pending?.total)} suffix=" F" color="#f59e0b" sub={`${data?.pending?.count || 0} commande(s)`} />
          </div>

          {/* Graphique CA (barres CSS) */}
          <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, padding: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: th.text, margin: '0 0 16px' }}>Évolution du chiffre d'affaires</p>
            {data?.series?.length ? (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180, overflowX: 'auto', paddingBottom: 4 }}>
                {data.series.map((s, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 32, flex: 1 }} title={`${s.date}: ${fmt(s.revenue)} F`}>
                    <div style={{ width: '100%', maxWidth: 40, height: `${Math.max(4, (s.revenue / maxRev) * 150)}px`, background: 'linear-gradient(180deg,#3b82f6,#1d4ed8)', borderRadius: '6px 6px 0 0', transition: 'height 0.3s' }} />
                    <span style={{ fontSize: 10, color: th.textMuted, whiteSpace: 'nowrap' }}>{s.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            ) : <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Aucune vente sur cette période</p>}
          </div>
        </>
      ) : (
        /* Vue Factures */
        invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: th.textMuted }}>
            <MdDownload size={48} style={{ opacity: 0.3 }} />
            <p style={{ marginTop: 12 }}>Aucune facture pour le moment</p>
            <p style={{ fontSize: 13 }}>Les factures sont générées automatiquement après chaque paiement confirmé.</p>
          </div>
        ) : (
          <div style={{ background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, overflow: 'hidden' }}>
            {invoices.map((inv, i) => (
              <div key={inv._id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, borderBottom: i < invoices.length - 1 ? `1px solid ${th.cardBorder}` : 'none' }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📄</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: th.text, margin: 0 }}>{inv.number} · <span style={{ color: th.textSub, fontWeight: 500 }}>{INV_TYPES[inv.type] || inv.type}</span></p>
                  <p style={{ fontSize: 13, color: th.textSub, margin: '2px 0 0' }}>{inv.customerName || inv.customerPhone || 'Client'} — {fmt(inv.total)} F</p>
                </div>
                <button onClick={() => downloadInvoicePdf(inv._id, inv.number)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer' }}>
                  <MdDownload size={16} /> PDF
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

// ═══════════════════ CONVERSATIONS WHATSAPP ═══════════════════
const ConversationsTab = ({ token, th, isDark }) => {
  const [list, setList] = useState([])
  const [selected, setSelected] = useState(null)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    apiClient(token).get('/merchant/conversations')
      .then(r => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false))
  }, [token])
  useEffect(() => { load() }, [load])

  const openConv = (sessionId) => {
    setSelected(sessionId); setDetail(null); setReply('')
    apiClient(token).get(`/merchant/conversations/${sessionId}`)
      .then(r => setDetail(r.data)).catch(() => {})
  }

  const sendReply = async () => {
    if (!reply.trim() || !selected) return
    setSending(true)
    try {
      await apiClient(token).post(`/merchant/conversations/${selected}/reply`, { message: reply.trim() })
      setReply('')
      openConv(selected) // recharger le détail
      load()
    } catch (e) { alert(e.response?.data?.error || 'Erreur lors de l\'envoi') }
    finally { setSending(false) }
  }

  const deleteConv = async (sessionId) => {
    if (!window.confirm('Supprimer cette conversation ? Cette action est définitive.')) return
    try {
      await apiClient(token).delete(`/merchant/conversations/${sessionId}`)
      if (selected === sessionId) { setSelected(null); setDetail(null) }
      load()
    } catch (e) { alert('Erreur lors de la suppression') }
  }

  const filtered = list.filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search))

  return (
    <div style={{ padding: 28 }}>
      <h2 style={{ color: th.text, fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Conversations WhatsApp</h2>
      <p style={{ color: th.textMuted, fontSize: 13, margin: '0 0 20px' }}>Les échanges entre vos clients et votre agent IA.</p>

      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 220px)', minHeight: 400 }}>
        {/* Colonne gauche : liste */}
        <div style={{ width: 300, flexShrink: 0, background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: `1px solid ${th.cardBorder}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: th.inputBg, border: `1px solid ${th.inputBorder}`, borderRadius: 10, padding: '0 12px' }}>
              <MdSearch size={18} style={{ color: th.textMuted }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
                style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: th.text, fontSize: 14, padding: '9px 0', minWidth: 0 }} />
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? <p style={{ color: th.textMuted, textAlign: 'center', padding: 30, fontSize: 13 }}>Chargement...</p>
              : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: th.textMuted }}>
                  <p style={{ fontWeight: 600, margin: 0 }}>Aucune conversation</p>
                  <p style={{ fontSize: 12, margin: '4px 0 0' }}>Les messages WhatsApp apparaîtront ici</p>
                </div>
              ) : filtered.map(c => (
                <div key={c.sessionId} onClick={() => openConv(c.sessionId)}
                  style={{ padding: '12px 14px', borderBottom: `1px solid ${th.cardBorder}`, cursor: 'pointer', background: selected === c.sessionId ? (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.06)') : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#25D366,#128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                      {(c.name || c.phone)[0]?.toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 600, color: th.text, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || c.phone}</p>
                      <p style={{ fontSize: 12, color: th.textSub, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.lastRole === 'assistant' ? '🤖 ' : ''}{c.lastMessage}
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                      <span style={{ fontSize: 10, color: th.textMuted }}>{timeAgo(c.lastActivity)}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteConv(c.sessionId) }} title="Supprimer"
                        style={{ border: 'none', background: 'transparent', color: th.textMuted, cursor: 'pointer', padding: 2, display: 'flex' }}>
                        <MdDelete size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <button onClick={load} style={{ padding: 12, border: 'none', borderTop: `1px solid ${th.cardBorder}`, background: 'transparent', color: th.textSub, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            🔄 Actualiser
          </button>
        </div>

        {/* Colonne droite : détail */}
        <div style={{ flex: 1, background: th.card, border: `1px solid ${th.cardBorder}`, borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: th.textMuted }}>
              <MdChat size={48} style={{ opacity: 0.3 }} />
              <p style={{ marginTop: 12 }}>Sélectionnez une conversation</p>
            </div>
          ) : !detail ? (
            <p style={{ color: th.textMuted, textAlign: 'center', padding: 40 }}>Chargement...</p>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: `1px solid ${th.cardBorder}` }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg,#25D366,#128C7E)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
                  {(detail.name || detail.phone)[0]?.toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, color: th.text, margin: 0 }}>{detail.name || detail.phone}</p>
                  <p style={{ fontSize: 12, color: th.textMuted, margin: 0 }}>{detail.phone}</p>
                </div>
                <button onClick={() => deleteConv(detail.sessionId)} title="Supprimer la conversation"
                  style={{ width: 36, height: 36, borderRadius: 9, border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MdDelete size={18} />
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {detail.history.map((m, i) => (
                  <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-start' : 'flex-end', maxWidth: '75%' }}>
                    <div style={{ padding: '10px 14px', borderRadius: 14, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                      background: m.role === 'user' ? (isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9') : (m.agentUsed === 'humain' ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : 'linear-gradient(135deg,#25D366,#128C7E)'),
                      color: m.role === 'user' ? th.text : '#fff' }}>
                      {m.content}
                    </div>
                    <p style={{ fontSize: 10, color: th.textMuted, margin: '3px 0 0', textAlign: m.role === 'user' ? 'left' : 'right' }}>
                      {m.agentUsed === 'humain' ? '👤 Vous · ' : ''}{new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
              {/* Zone de réponse manuelle */}
              <div style={{ display: 'flex', gap: 8, padding: 14, borderTop: `1px solid ${th.cardBorder}` }}>
                <input value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
                  placeholder="Répondre au client..." disabled={sending}
                  style={{ flex: 1, padding: '11px 14px', borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.text, fontSize: 14, outline: 'none' }} />
                <button onClick={sendReply} disabled={sending || !reply.trim()}
                  style={{ padding: '0 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#25D366,#128C7E)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: sending || !reply.trim() ? 0.6 : 1 }}>
                  <MdSend size={18} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════ DASHBOARD PRINCIPAL ═══════════════════
export default function MerchantDashboard() {
  const [theme, setTheme] = useState(localStorage.getItem('ocass-theme') || 'dark')
  const isDark = theme === 'dark'
  const th = getTheme(isDark)
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || null)
  const [merchant, setMerchant] = useState(null)
  const [tab, setTab] = useState('overview')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [checking, setChecking] = useState(true)
  const [subStatus, setSubStatus] = useState(null)
  const [showPlayground, setShowPlayground] = useState(false)
  const [notifs, setNotifs] = useState([])
  const [notifUnread, setNotifUnread] = useState(0)
  const [showNotifs, setShowNotifs] = useState(false)

  // Vérifier le token au montage
  useEffect(() => {
    if (!token) { setChecking(false); return }
    apiClient(token).get('/merchant/me')
      .then(r => setMerchant(r.data))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null) })
      .finally(() => setChecking(false))
  }, [token])

  // Charger le statut d'abonnement
  useEffect(() => {
    if (!token || !merchant) return
    apiClient(token).get('/subscription/me').then(r => setSubStatus(r.data.status)).catch(() => {})
  }, [token, merchant])

  // Charger les notifications (+ rafraîchir toutes les 60s)
  const loadNotifs = useCallback(() => {
    if (!token) return
    apiClient(token).get('/merchant/notifications')
      .then(r => { setNotifs(r.data.items || []); setNotifUnread(r.data.unread || 0) })
      .catch(() => {})
  }, [token])
  useEffect(() => {
    if (!token || !merchant) return
    loadNotifs()
    const id = setInterval(loadNotifs, 60000)
    return () => clearInterval(id)
  }, [token, merchant, loadNotifs])

  const markNotifsRead = () => {
    apiClient(token).post('/merchant/notifications/read').then(() => { setNotifUnread(0); loadNotifs() }).catch(() => {})
  }
  const markOneRead = (id) => {
    setNotifs(prev => prev.map(n => n._id === id ? { ...n, readAt: new Date().toISOString() } : n))
    setNotifUnread(u => Math.max(0, u - 1))
    apiClient(token).post(`/merchant/notifications/${id}/read`).catch(() => {})
  }

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark'
    setTheme(next); localStorage.setItem('ocass-theme', next)
  }
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY); setToken(null); setMerchant(null)
    window.location.href = 'https://agent-ia-senegal.com/'
  }
  const onLogin = (tok, merch) => { setToken(tok); setMerchant(merch) }

  if (checking) return <div style={{ minHeight: '100vh', background: th.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 40, height: 40, border: '3px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>
  if (!token || !merchant) return <OcassAuth onLogin={onLogin} />

  const navItems = [
    { id: 'overview', icon: <MdDashboard size={18} />, label: "Vue d'ensemble" },
    { id: 'conversations', icon: <MdChat size={18} />, label: 'Conversations' },
    { id: 'products', icon: <MdInventory size={18} />, label: 'Produits' },
    { id: 'shops', icon: <MdSwapHoriz size={18} />, label: 'Magasins' },
    { id: 'orders', icon: <MdShoppingCart size={18} />, label: 'Commandes' },
    { id: 'payments', icon: <MdPayments size={18} />, label: 'Paiements' },
    { id: 'accounting', icon: <MdTrendingUp size={18} />, label: 'Comptabilité' },
    { id: 'approvals', icon: <MdGavel size={18} />, label: 'Demandes de prix' },
    { id: 'notifications', icon: <MdNotificationsNone size={18} />, label: 'Notifications' },
    { id: 'employees', icon: <MdPeople size={18} />, label: 'Employés' },
    { id: 'subscription', icon: <MdWorkspacePremium size={18} />, label: 'Mon abonnement' },
    { id: 'settings', icon: <MdSettings size={18} />, label: 'Paramètres' },
  ]

  const renderTab = () => {
    switch (tab) {
      case 'overview': return <Overview token={token} th={th} />
      case 'products': return <Products token={token} th={th} isDark={isDark} merchant={merchant} />
      case 'shops': return <Shops token={token} th={th} isDark={isDark} />
      case 'orders': return <Orders token={token} th={th} />
      case 'payments': return <Payments token={token} th={th} />
      case 'approvals': return <Approvals token={token} th={th} />
      case 'employees': return <Employees token={token} th={th} isDark={isDark} />
      case 'notifications': return <NotificationsTab token={token} th={th} isDark={isDark} />
      case 'accounting': return <AccountingTab token={token} th={th} isDark={isDark} />
      case 'conversations': return <ConversationsTab token={token} th={th} isDark={isDark} />
      case 'subscription': return <SubscriptionTab token={token} th={th} subStatus={subStatus} />
      case 'settings': return <SettingsTab token={token} th={th} />
      default: return null
    }
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: th.bg, fontFamily: "'DM Sans', sans-serif" }}>
      {/* Style global : fix lisibilité des options en dark mode */}
      <style>{`
        select option { background: ${th.optionBg} !important; color: ${th.optionText} !important; }
        select { color: ${th.text}; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Sidebar */}
      <div style={{ width: sidebarOpen ? 240 : 68, flexShrink: 0, background: th.sidebar, borderRight: `1px solid ${th.sidebarBorder}`, display: 'flex', flexDirection: 'column', transition: 'width 0.25s', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 40 }}>
        <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto' }}>
          {navItems.map(item => (
            <button key={item.id} onClick={() => setTab(item.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: sidebarOpen ? 10 : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center', padding: sidebarOpen ? '8px 12px' : '8px 0', borderRadius: 10, border: 'none', cursor: 'pointer', background: tab === item.id ? th.navActive : 'transparent', color: tab === item.id ? '#fff' : th.textSub, transition: 'all 0.15s' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.icon}</div>
              {sidebarOpen && <span style={{ fontSize: 14, fontWeight: 500 }}>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Encart abonnement (en bas de la sidebar, cliquable) */}
        {sidebarOpen && subStatus && (
          <div onClick={() => setTab('subscription')} style={{ margin: '0 8px 8px', padding: 12, borderRadius: 12, cursor: 'pointer',
            background: subStatus.kind === 'trial' ? 'rgba(245,158,11,0.12)' : subStatus.kind === 'paid' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.12)',
            border: `1px solid ${subStatus.kind === 'trial' ? 'rgba(245,158,11,0.3)' : subStatus.kind === 'paid' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <MdWorkspacePremium size={16} color={subStatus.kind === 'trial' ? '#f59e0b' : subStatus.kind === 'paid' ? '#10b981' : '#ef4444'} />
              <span style={{ fontSize: 12, fontWeight: 700, color: subStatus.kind === 'trial' ? '#f59e0b' : subStatus.kind === 'paid' ? '#10b981' : '#ef4444' }}>
                {subStatus.kind === 'trial' ? 'Essai gratuit' : subStatus.kind === 'paid' ? `Plan ${subStatus.plan}` : 'Abonnement expiré'}
              </span>
            </div>
            <p style={{ fontSize: 11, color: th.textSub, margin: 0, lineHeight: 1.4 }}>
              {subStatus.kind === 'expired' ? 'Cliquez pour réactiver' : `${subStatus.daysLeft} jour${subStatus.daysLeft > 1 ? 's' : ''} restant${subStatus.daysLeft > 1 ? 's' : ''} · gérer`}
            </p>
          </div>
        )}

        <div style={{ padding: 8, borderTop: `1px solid ${th.sidebarBorder}` }}>
          <button onClick={toggleTheme} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: sidebarOpen ? 10 : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center', padding: sidebarOpen ? '8px 12px' : '8px 0', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'transparent', color: th.textSub, marginBottom: 2 }}>
            {isDark ? <MdLightMode size={18} color="#f59e0b" /> : <MdDarkMode size={18} color="#6366f1" />}
            {sidebarOpen && <span style={{ fontSize: 14 }}>{isDark ? 'Thème clair' : 'Thème sombre'}</span>}
          </button>
          <button onClick={logout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: sidebarOpen ? 10 : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center', padding: sidebarOpen ? '8px 12px' : '8px 0', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'transparent', color: '#f87171' }}>
            <MdLogout size={18} />
            {sidebarOpen && <span style={{ fontSize: 14, fontWeight: 600 }}>Déconnexion</span>}
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, marginLeft: sidebarOpen ? 240 : 68, transition: 'margin-left 0.25s', minWidth: 0 }}>
        {/* Top bar : bienvenue + actions rapides */}
        <div style={{ height: 64, background: th.header, borderBottom: `1px solid ${th.sidebarBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', position: 'sticky', top: 0, zIndex: 30, backdropFilter: 'blur(12px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <button onClick={() => setSidebarOpen(s => !s)} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {sidebarOpen ? <MdChevronLeft size={20} /> : <MdChevronRight size={20} />}
            </button>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: th.text, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Bienvenue chez {merchant.businessName}</p>
              <p style={{ fontSize: 12, color: th.textMuted, margin: 0 }}>{navItems.find(n => n.id === tab)?.label}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button onClick={() => setShowPlayground(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              <MdChat size={18} /> Tester l'agent
            </button>
            <div style={{ position: 'relative' }}>
              <button title="Notifications" onClick={() => setShowNotifs(s => !s)}
                style={{ width: 38, height: 38, borderRadius: 10, border: `1px solid ${th.inputBorder}`, background: th.inputBg, color: th.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                <MdNotificationsNone size={20} />
                {notifUnread > 0 && <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18, borderRadius: 9, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{notifUnread > 9 ? '9+' : notifUnread}</span>}
              </button>
              {showNotifs && (
                <>
                  <div onClick={() => setShowNotifs(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
                  <div style={{ position: 'absolute', top: 46, right: 0, width: 340, maxHeight: 440, overflowY: 'auto', background: th.popover, border: `1px solid ${th.cardBorder}`, borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', zIndex: 50 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: `1px solid ${th.cardBorder}` }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: th.text }}>Notifications</span>
                      {notifs.filter(n => !n.readAt).length > 0
                        ? <button onClick={markNotifsRead} style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Tout marquer lu</button>
                        : <button onClick={() => { setTab('notifications'); setShowNotifs(false) }} style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Tout voir</button>}
                    </div>
                    {notifs.filter(n => !n.readAt).length === 0 ? (
                      <p style={{ padding: 24, textAlign: 'center', color: th.textMuted, fontSize: 13 }}>Aucune nouvelle notification</p>
                    ) : notifs.filter(n => !n.readAt).slice(0, 10).map(n => {
                      const meta = NOTIF_META[n.type] || NOTIF_META.custom
                      return (
                        <div key={n._id} onClick={() => markOneRead(n._id)} title="Marquer comme lu"
                          style={{ display: 'flex', gap: 10, padding: '11px 16px', borderBottom: `1px solid ${th.cardBorder}`, cursor: 'pointer', background: isDark ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.05)' }}>
                          <span style={{ fontSize: 18, flexShrink: 0 }}>{meta.icon}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: th.text, margin: 0 }}>{meta.title}</p>
                            <p style={{ fontSize: 12, color: th.textSub, margin: '2px 0 0', lineHeight: 1.4 }}>{n.message}</p>
                            <p style={{ fontSize: 11, color: th.textMuted, margin: '3px 0 0' }}>{timeAgo(n.createdAt)}</p>
                          </div>
                          <MdClose size={14} style={{ color: th.textMuted, flexShrink: 0 }} />
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
            </div>
            {/* Logo du commerçant (sans le nom, déjà dans le message de bienvenue) */}
            <div style={{ width: 40, height: 40, borderRadius: 12, background: merchant.logo ? 'transparent' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', border: `1px solid ${th.cardBorder}` }}>
              {merchant.logo
                ? <img src={merchant.logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
                : <MdStorefront size={22} color="white" />}
            </div>
          </div>
        </div>

        <main>{renderTab()}</main>
      </div>

      {/* Modale Playground */}
      {showPlayground && (
        <div onClick={() => setShowPlayground(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 60, backdropFilter: 'blur(3px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, height: '80vh', maxHeight: 700, background: isDark ? '#0f172a' : '#fff', borderRadius: 20, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${th.sidebarBorder}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MdChat size={20} color="#fff" /></div>
                <div><p style={{ fontWeight: 700, fontSize: 15, color: th.text, margin: 0 }}>Tester mon agent IA</p><p style={{ fontSize: 12, color: th.textMuted, margin: 0 }}>Simulez une conversation client</p></div>
              </div>
              <button onClick={() => setShowPlayground(false)} style={{ width: 34, height: 34, borderRadius: 8, border: 'none', background: th.inputBg, color: th.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MdClose size={20} /></button>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <Playground token={token} th={th} isDark={isDark} embedded />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
